import { fetchRefreshShipmentTracking } from '@/entities/rma';
import { fetchBiodirectInfoDrawer } from '@/entities/study';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { showMessageError } from '@/shared/lib';
import _ from 'lodash';
import { useEffect } from 'react';
import type { BiodirectInfoDrawerProps, Event } from './biodirectInfoDrawer.types';

/**
 * Drives the studies-list Biodirect information drawer: opening on a selected study, loading that study's Biodirect shipment record and refreshing carrier tracking. Opens the drawer whenever a non-empty study arrives, fetches the record on that transition, and exposes the tracking refresh, close and scroll handlers together with the raw state setter and the caller's onClose.
 *
 * @param study - Study whose Biodirect shipment the drawer displays.
 * @param onClose - Callback forwarded back to the drawer for its close action.
 * @returns The drawer state and setter with the tracking-refresh, close and scroll handlers plus onClose.
 */
export const useBiodirectInfoDrawer = ({ study, onClose }: BiodirectInfoDrawerProps) => {
  const [state, setState] = useMergeState<{
    isScroll: boolean;
    isLoading: boolean;
    biofluxDirect: any;
    openDrawer: boolean;
  }>({
    isScroll: false,
    isLoading: false,
    biofluxDirect: null,
    openDrawer: false,
  });

  /**
   * Loads the Biodirect shipment record for the study the drawer was opened with. Fetches it by study.biofluxDirect.id while isLoading is set, stores the result as biofluxDirect, logs a failure without surfacing it to the user, and clears the loading flag either way.
   *
   * @returns Resolves once the Biodirect record has been stored or the failure logged.
   */
  const handleDataLoad = async () => {
    try {
      setState({ isLoading: true });
      const fetchedBiofluxDirect = await fetchBiodirectInfoDrawer(study.biofluxDirect.id);
      setState({ biofluxDirect: fetchedBiofluxDirect });
    } catch (error) {
      console.error('Failed to fetch Biodirect info: ', error);
    } finally {
      setState({ isLoading: false });
    }
  };

  /**
   * Refetches carrier tracking for the Biodirect shipment's latest label and merges it into the drawer's record. Clones the record, picks senderInformation.latestLabel for OutboundBiodirect and recipientInformation.latestLabel otherwise, writes the fetched tracking onto it, reports failures through showMessageError, and always invokes callback when it is a function.
   *
   * @param type - Shipment direction, 'OutboundBiodirect' for the sender label or anything else for the recipient label.
   * @param transactionId - Carrier transaction identifier of the label to refresh.
   * @param ticketId - Identifier of the ticket owning the label.
   * @param callback - Optional function invoked once the refresh settles.
   * @returns Resolves once the refreshed tracking has been merged into state.
   */
  const handleRefreshTracking = async ({
    type,
    transactionId,
    ticketId,
    callback,
  }: {
    type: string;
    transactionId: string;
    ticketId: string;
    callback?: () => void;
  }) => {
    try {
      const tracking = await fetchRefreshShipmentTracking({
        transactionId,
        ticketId,
      });
      const clonedBiofluxDirect = _.cloneDeep(state.biofluxDirect);
      const latestLabel =
        type === 'OutboundBiodirect'
          ? clonedBiofluxDirect?.senderInformation?.latestLabel
          : clonedBiofluxDirect?.recipientInformation?.latestLabel;
      if (latestLabel) {
        latestLabel.tracking = tracking;
      }
      setState({ biofluxDirect: clonedBiofluxDirect });
    } catch (error) {
      console.error('Failed to refresh tracking: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    } finally {
      if (_.isFunction(callback)) {
        callback();
      }
    }
  };

  /**
   * Closes the study's Biodirect information drawer. Sets openDrawer to false, the flag the drawer's visibility and its data reload key off.
   *
   * @returns Nothing.
   */
  const handleCloseButtonClick = () => {
    setState({ openDrawer: false });
  };

  /**
   * Tracks whether the Biodirect information drawer body is scrolled away from the top. Reads scrollTop off the scroll event's target and writes isScroll only when it disagrees with the stored flag.
   *
   * @param e - Scroll event carrying the scrolled element as its target.
   * @returns Nothing.
   */
  const handleScroll = (e: Event) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && state.isScroll) {
      setState({ isScroll: false });
    } else if (scrollTop !== 0 && !state.isScroll) {
      setState({ isScroll: true });
    }
  };

  useEffect(() => {
    if (!_.isEmpty(study)) {
      setState({ openDrawer: true });
    }
  }, [study]);

  useUpdateEffect(() => {
    if (state.openDrawer) {
      handleDataLoad();
    }
  }, [state.openDrawer]);

  return {
    state,
    setState,
    handleRefreshTracking,
    handleCloseButtonClick,
    handleScroll,
    onClose,
  };
};
